import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LeadForm = ({ source, sessionId = null, onSuccess, compact = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    whatsapp: '',
    email: '',
    goal: '',
    country_interest: '',
    move_timeline: '',
    qualification: '',
    employment_status: '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.full_name || !formData.whatsapp || !formData.email || !formData.goal) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // WhatsApp validation (10 digits)
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(formData.whatsapp)) {
      toast.error('Please enter a valid 10-digit WhatsApp number');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...formData,
        source,
        session_id: sessionId,
      });
      
      toast.success('Successfully registered! We\'ll be in touch soon.');
      
      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/thank-you', { state: { source, name: formData.full_name } });
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const goals = ['Study', 'Work', 'Settle', 'Ausbildung', 'Not Sure'];
  const countries = [
    'Germany', 'Netherlands', 'France', 'Spain', 'Portugal', 
    'Italy', 'Poland', 'Belgium', 'Sweden', 'Finland', 'Other EU'
  ];
  const timelines = ['0-6 months', '6-12 months', '1-2 years', 'Just exploring'];
  const qualifications = ['10th', '12th', 'Diploma', "Bachelor's", "Master's", 'PhD'];
  const employmentOptions = ['Yes', 'No', 'Student'];

  return (
    <form onSubmit={handleSubmit} data-testid="lead-form" className="space-y-4">
      <div className={compact ? 'space-y-4' : 'grid grid-cols-1 md:grid-cols-2 gap-4'}>
        {/* Full Name */}
        <div className="space-y-2">
          <Label htmlFor="full_name">Full Name *</Label>
          <Input
            id="full_name"
            data-testid="input-full-name"
            placeholder="Enter your full name"
            value={formData.full_name}
            onChange={(e) => handleChange('full_name', e.target.value)}
            className="h-12 rounded-lg border-gray-200 focus:ring-2 focus:ring-[#002A76]/20 focus:border-[#002A76]"
            required
          />
        </div>

        {/* WhatsApp */}
        <div className="space-y-2">
          <Label htmlFor="whatsapp">WhatsApp Number *</Label>
          <Input
            id="whatsapp"
            data-testid="input-whatsapp"
            type="tel"
            placeholder="10-digit number"
            value={formData.whatsapp}
            onChange={(e) => handleChange('whatsapp', e.target.value.replace(/\D/g, '').slice(0, 10))}
            className="h-12 rounded-lg border-gray-200 focus:ring-2 focus:ring-[#002A76]/20 focus:border-[#002A76]"
            required
          />
        </div>

        {/* Email */}
        <div className="space-y-2">
          <Label htmlFor="email">Email ID *</Label>
          <Input
            id="email"
            data-testid="input-email"
            type="email"
            placeholder="your@email.com"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            className="h-12 rounded-lg border-gray-200 focus:ring-2 focus:ring-[#002A76]/20 focus:border-[#002A76]"
            required
          />
        </div>

        {/* Goal */}
        <div className="space-y-2">
          <Label>What is your goal? *</Label>
          <Select value={formData.goal} onValueChange={(value) => handleChange('goal', value)}>
            <SelectTrigger data-testid="select-goal" className="h-12 rounded-lg">
              <SelectValue placeholder="Select your goal" />
            </SelectTrigger>
            <SelectContent>
              {goals.map((goal) => (
                <SelectItem key={goal} value={goal}>{goal}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Country Interest */}
        <div className="space-y-2">
          <Label>Which country interests you?</Label>
          <Select value={formData.country_interest} onValueChange={(value) => handleChange('country_interest', value)}>
            <SelectTrigger data-testid="select-country" className="h-12 rounded-lg">
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent>
              {countries.map((country) => (
                <SelectItem key={country} value={country}>{country}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Move Timeline */}
        <div className="space-y-2">
          <Label>When are you planning to move?</Label>
          <Select value={formData.move_timeline} onValueChange={(value) => handleChange('move_timeline', value)}>
            <SelectTrigger data-testid="select-timeline" className="h-12 rounded-lg">
              <SelectValue placeholder="Select timeline" />
            </SelectTrigger>
            <SelectContent>
              {timelines.map((timeline) => (
                <SelectItem key={timeline} value={timeline}>{timeline}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Qualification */}
        <div className="space-y-2">
          <Label>Highest Qualification</Label>
          <Select value={formData.qualification} onValueChange={(value) => handleChange('qualification', value)}>
            <SelectTrigger data-testid="select-qualification" className="h-12 rounded-lg">
              <SelectValue placeholder="Select qualification" />
            </SelectTrigger>
            <SelectContent>
              {qualifications.map((qual) => (
                <SelectItem key={qual} value={qual}>{qual}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Employment Status */}
        <div className="space-y-2">
          <Label>Currently employed?</Label>
          <Select value={formData.employment_status} onValueChange={(value) => handleChange('employment_status', value)}>
            <SelectTrigger data-testid="select-employment" className="h-12 rounded-lg">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              {employmentOptions.map((option) => (
                <SelectItem key={option} value={option}>{option}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Button
        type="submit"
        data-testid="submit-lead-form"
        disabled={loading}
        className="w-full h-12 bg-[#002A76] hover:bg-[#001F5C] text-white rounded-full font-semibold text-base btn-hover-lift mt-6"
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Submitting...
          </>
        ) : (
          'Submit & Get Started'
        )}
      </Button>

      <p className="text-xs text-gray-500 text-center mt-4">
        By submitting, you agree to receive communication from TieClad. No spam, ever.
      </p>
    </form>
  );
};

export default LeadForm;
